/**
 * Freehand Drawing Tool - SVG Path Rendering
 * Convert path data to SVG and render with advanced stroke properties
 */

import { vec2 } from './geometry';

// ============================================================================
// SVG Path Data Generation
// ============================================================================

export function pathToSvgD(path) {
  const { nodes, segments, closed } = path;
  
  if (nodes.length === 0) return '';
  
  let d = `M ${nodes[0].p.x} ${nodes[0].p.y}`;
  
  for (const segment of segments) {
    const fromNode = nodes[segment.from];
    const toNode = nodes[segment.to];
    
    if (!fromNode || !toNode) continue;
    
    if (segment.type === 'line') {
      d += ` L ${toNode.p.x} ${toNode.p.y}`;
    } else if (segment.type === 'cubic') {
      const c1 = fromNode.h2 || fromNode.p;
      const c2 = toNode.h1 || toNode.p;
      d += ` C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${toNode.p.x} ${toNode.p.y}`;
    }
  }
  
  if (closed) {
    d += ' Z';
  }
  
  return d;
}

// ============================================================================
// Stroke Style Conversion
// ============================================================================

export function strokeStyleToSvgProps(stroke) {
  const props = {
    stroke: stroke.color,
    strokeWidth: stroke.width,
    fill: 'none'
  };
  
  if (stroke.lineCap) {
    props.strokeLinecap = stroke.lineCap;
  }
  
  if (stroke.lineJoin) {
    props.strokeLinejoin = stroke.lineJoin;
  }
  
  if (stroke.miterLimit) {
    props.strokeMiterlimit = stroke.miterLimit;
  }
  
  if (stroke.dash && stroke.dash.length > 0) {
    props.strokeDasharray = stroke.dash.join(' ');
  }
  
  if (stroke.opacity !== undefined) {
    props.strokeOpacity = stroke.opacity;
  }
  
  if (stroke.nonScaling) {
    props.vectorEffect = 'non-scaling-stroke';
  }
  
  return props;
}

// ============================================================================
// Arrow Markers
// ============================================================================

export function createArrowMarker(id, type, color, size = 10) {
  if (type === 'none') return '';
  
  const markerSize = size;
  const refX = type === 'triangle' ? markerSize : markerSize / 2;
  
  let path = '';
  
  switch (type) {
    case 'triangle':
      path = `<path d="M 0 0 L ${markerSize} ${markerSize/2} L 0 ${markerSize} Z" fill="${color}"/>`;
      break;
      
    case 'arrow':
      path = `<path d="M 0 0 L ${markerSize} ${markerSize/2} L ${markerSize*0.7} ${markerSize/2} L ${markerSize*0.7} ${markerSize*0.8} L 0 ${markerSize*0.8} L 0 ${markerSize*0.2} L ${markerSize*0.7} ${markerSize*0.2} L ${markerSize*0.7} ${markerSize/2} Z" fill="${color}"/>`;
      break;
      
    case 'diamond':
      path = `<path d="M 0 ${markerSize/2} L ${markerSize/2} 0 L ${markerSize} ${markerSize/2} L ${markerSize/2} ${markerSize} Z" fill="${color}"/>`;
      break;
      
    case 'circle':
      path = `<circle cx="${markerSize/2}" cy="${markerSize/2}" r="${markerSize/3}" fill="${color}"/>`;
      break;
  }
  
  return `
    <marker id="${id}" 
            markerWidth="${markerSize}" 
            markerHeight="${markerSize}" 
            refX="${refX}" 
            refY="${markerSize/2}" 
            orient="auto" 
            markerUnits="strokeWidth">
      ${path}
    </marker>
  `;
}

export function getArrowMarkerIds(path) {
  const ids = {};
  
  if (path.stroke.startArrow && path.stroke.startArrow !== 'none') {
    ids.start = `arrow-start-${path.id}`;
  }
  
  if (path.stroke.endArrow && path.stroke.endArrow !== 'none') {
    ids.end = `arrow-end-${path.id}`;
  }
  
  return ids;
}

// ============================================================================
// Complete SVG Generation
// ============================================================================

export function pathToSvgElement(path, includeMarkers = true) {
  const d = pathToSvgD(path);
  const strokeProps = strokeStyleToSvgProps(path.stroke);
  
  // Convert props to attribute string
  const attributes = Object.entries(strokeProps)
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ');
  
  let pathElement = `<path d="${d}" ${attributes}`;
  
  // Add arrow markers
  let markers = '';
  if (includeMarkers) {
    const markerIds = getArrowMarkerIds(path);
    
    if (markerIds.start) {
      pathElement += ` marker-start="url(#${markerIds.start})"`;
      markers += createArrowMarker(
        markerIds.start, 
        path.stroke.startArrow, 
        path.stroke.color, 
        path.stroke.width * 2
      );
    }
    
    if (markerIds.end) {
      pathElement += ` marker-end="url(#${markerIds.end})"`;
      markers += createArrowMarker(
        markerIds.end, 
        path.stroke.endArrow, 
        path.stroke.color, 
        path.stroke.width * 2
      );
    }
  }
  
  pathElement += '/>';
  
  return { pathElement, markers };
}

// ============================================================================
// Node and Handle Visualization
// ============================================================================

export function renderNodeHandles(node, selected = false) {
  const elements = [];
  
  // Render handles
  if (node.h1) {
    elements.push(`
      <line x1="${node.p.x}" y1="${node.p.y}" 
            x2="${node.h1.x}" y2="${node.h1.y}" 
            stroke="#666" stroke-width="1" stroke-dasharray="2,2"/>
      <circle cx="${node.h1.x}" cy="${node.h1.y}" r="3" 
              fill="#666" stroke="#333" stroke-width="1"/>
    `);
  }
  
  if (node.h2) {
    elements.push(`
      <line x1="${node.p.x}" y1="${node.p.y}" 
            x2="${node.h2.x}" y2="${node.h2.y}" 
            stroke="#666" stroke-width="1" stroke-dasharray="2,2"/>
      <circle cx="${node.h2.x}" cy="${node.h2.y}" r="3" 
              fill="#666" stroke="#333" stroke-width="1"/>
    `);
  }
  
  // Render anchor point
  const nodeColor = selected ? '#ff0000' : '#0066cc';
  const nodeSize = selected ? 5 : 4;
  
  elements.push(`
    <circle cx="${node.p.x}" cy="${node.p.y}" r="${nodeSize}" 
            fill="${nodeColor}" stroke="#fff" stroke-width="2"/>
  `);
  
  return elements.join('\n');
}

export function renderPathNodes(path, selectedNodes = new Set()) {
  return path.nodes
    .map((node, index) => renderNodeHandles(node, selectedNodes.has(index)))
    .join('\n');
}

// ============================================================================
// Snap Indicators
// ============================================================================

export function renderSnapIndicator(point, type) {
  const colors = {
    grid: '#00ff00',
    endpoint: '#ff0000',
    midpoint: '#0000ff',
    angle: '#ff8800',
    guide: '#8800ff'
  };
  
  const color = colors[type] || '#666666';
  
  return `
    <circle cx="${point.x}" cy="${point.y}" r="6" 
            fill="none" stroke="${color}" stroke-width="2">
      <animate attributeName="r" values="6;10;6" dur="1s" repeatCount="indefinite"/>
      <animate attributeName="stroke-opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite"/>
    </circle>
  `;
}

// ============================================================================
// Utility Functions
// ============================================================================

export function calculatePathLength(path) {
  let totalLength = 0;
  
  path.segments.forEach(segment => {
    const fromNode = path.nodes[segment.from];
    const toNode = path.nodes[segment.to];
    
    if (!fromNode || !toNode) return;
    
    if (segment.type === 'line') {
      totalLength += vec2.distance(fromNode.p, toNode.p);
    } else if (segment.type === 'cubic') {
      // Approximate cubic bezier length
      const c1 = fromNode.h2 || fromNode.p;
      const c2 = toNode.h1 || toNode.p;
      
      // Simple approximation using control polygon
      totalLength += vec2.distance(fromNode.p, c1) + 
                    vec2.distance(c1, c2) + 
                    vec2.distance(c2, toNode.p);
    }
  });
  
  return totalLength;
}

export function getPathBoundingBox(path) {
  if (path.nodes.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  
  let minX = path.nodes[0].p.x;
  let minY = path.nodes[0].p.y;
  let maxX = path.nodes[0].p.x;
  let maxY = path.nodes[0].p.y;
  
  path.nodes.forEach(node => {
    minX = Math.min(minX, node.p.x);
    minY = Math.min(minY, node.p.y);
    maxX = Math.max(maxX, node.p.x);
    maxY = Math.max(maxY, node.p.y);
    
    if (node.h1) {
      minX = Math.min(minX, node.h1.x);
      minY = Math.min(minY, node.h1.y);
      maxX = Math.max(maxX, node.h1.x);
      maxY = Math.max(maxY, node.h1.y);
    }
    
    if (node.h2) {
      minX = Math.min(minX, node.h2.x);
      minY = Math.min(minY, node.h2.y);
      maxX = Math.max(maxX, node.h2.x);
      maxY = Math.max(maxY, node.h2.y);
    }
  });
  
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}
